/**
 * Portfolio module helpers — pure, UI-facing utilities for the create/edit
 * flow. The authoritative sanitization lives in `portfolioProjects.service`
 * (the storage boundary); these are for client-side validation and display.
 */
import {
  LIMITS,
  ACCEPTED_FILE_TYPES,
  PROJECT_STATUS,
  RUNNABLE_SNIPPET_LANGUAGES,
  VISIBILITY,
  PROJECT_TYPE,
  USERNAME,
  RESERVED_USERNAMES,
} from './constants';

/** True when `value` is a well-formed absolute http(s) URL. */
export function isHttpUrl(value = '') {
  try {
    const url = new URL(String(value).trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/** A student may (re)submit for review from a draft or after changes requested. */
export function canRequestReview(status) {
  return status === PROJECT_STATUS.DRAFT || status === PROJECT_STATUS.CHANGES_REQUESTED;
}

/** The owner may edit content in every state except once it's approved (locked). */
export function isEditableStatus(status) {
  return status !== PROJECT_STATUS.APPROVED;
}

/**
 * Which runner a code snippet should be handed to, or `null` when it is only
 * ever shown as source. A `code_snippet` with non-empty content in a language
 * the sandbox can execute (see `RUNNABLE_SNIPPET_LANGUAGES`) runs live in an
 * isolated sandbox (`SNIPPET_SANDBOX`) instead of being printed.
 */
export function snippetRunKind(project) {
  if (project?.type !== PROJECT_TYPE.CODE_SNIPPET) return null;
  if (!String(project?.content || '').trim()) return null;
  const language = String(project?.contentLanguage || '').toLowerCase();
  return RUNNABLE_SNIPPET_LANGUAGES[language] || null;
}

/** True when a snippet runs live at all, in any supported language. */
export function isRunnableSnippet(project) {
  return snippetRunKind(project) !== null;
}

/**
 * The student's example replies to their own `input()` prompts, one per line,
 * as the array the runner consumes. Blank lines are kept — an empty answer is a
 * legitimate reply — except a single trailing one left by the textarea.
 */
export function sampleAnswers(project) {
  const raw = String(project?.sampleInput || '');
  if (!raw) return [];
  return raw.replace(/\n$/, '').split('\n');
}

/** The bundled data files a runnable snippet can read, safe to hand the runner. */
export function projectDataFiles(project) {
  if (!Array.isArray(project?.dataFiles)) return [];
  return project.dataFiles.filter((file) => file?.name && typeof file.content === 'string');
}

/** True when a project is live on the public portfolio. */
export function isPublic(project) {
  return (
    project?.visibility === VISIBILITY.PUBLIC && project?.status === PROJECT_STATUS.APPROVED
  );
}

/** First name only (privacy for minors) — the only name shown publicly later. */
export function firstNameOf(fullName = '') {
  const first = String(fullName).trim().split(/\s+/)[0];
  return first || 'Student';
}

/** Canonical stored form of a typed username: trimmed + lowercased. */
export function normalizeUsername(value = '') {
  return String(value).trim().toLowerCase();
}

/**
 * Validate a student-chosen public username (the /p/{username} slug). Returns a
 * short error message to show the student, or `null` when the format is valid.
 * Uniqueness is a separate check against the registry (`isUsernameAvailable`).
 */
export function validateUsername(value = '') {
  const handle = normalizeUsername(value);
  if (!handle) return 'Choose a username.';
  if (handle.length < USERNAME.min) return `Use at least ${USERNAME.min} characters.`;
  if (handle.length > USERNAME.max) return `Keep it to ${USERNAME.max} characters or fewer.`;
  if (!USERNAME.pattern.test(handle)) {
    return 'Use lowercase letters, numbers and hyphens; start with a letter.';
  }
  if (RESERVED_USERNAMES.includes(handle)) return 'That username isn’t available.';
  return null;
}

/** Whether an uploaded File is an accepted kind + within the size cap. */
export function validateUploadFile(file) {
  if (!file) return 'Please choose a file.';
  if (file.size > LIMITS.fileBytes) {
    return `File is too large (max ${Math.round(LIMITS.fileBytes / (1024 * 1024))} MB).`;
  }
  // Some browsers report zip as application/octet-stream — accept by extension too.
  const isZipByName = /\.zip$/i.test(file.name || '');
  if (!ACCEPTED_FILE_TYPES.includes(file.type) && !isZipByName) {
    return 'Unsupported file type. Use an image, PDF, or zip.';
  }
  return null;
}

/** Human-readable byte size, e.g. 1536 → "1.5 KB". */
export function humanSize(bytes = 0) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB'];
  let size = n / 1024;
  let i = 0;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i += 1;
  }
  return `${size.toFixed(size < 10 ? 1 : 0)} ${units[i]}`;
}

/**
 * Best-effort attempt to discover a preview image (Open Graph / Twitter card)
 * for a deployed project URL, so the student doesn't have to upload a thumbnail
 * by hand.
 *
 * This runs in the browser, so cross-origin sites that don't send permissive
 * CORS headers will block the fetch — that is expected and NOT an error: the
 * caller falls back to a manual thumbnail upload or the card placeholder. The
 * function therefore NEVER throws and NEVER blocks saving; it resolves to a
 * safe https image URL or `null`.
 *
 * (A server-side fetcher could make this reliable for all sites; deferred to a
 * later phase to avoid adding backend surface now.)
 */
export async function fetchPreviewImage(pageUrl, { timeoutMs = 6000 } = {}) {
  if (!isHttpUrl(pageUrl)) return null;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(pageUrl, { signal: controller.signal, mode: 'cors' });
    if (!res.ok) return null;

    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const pick = (selector) =>
      doc.querySelector(selector)?.getAttribute('content')?.trim() || null;

    const found =
      pick('meta[property="og:image"]') ||
      pick('meta[name="og:image"]') ||
      pick('meta[property="twitter:image"]') ||
      pick('meta[name="twitter:image"]') ||
      pick('link[rel="image_src"]');

    if (!found) return null;

    // Resolve protocol-relative / relative URLs against the page, and only ever
    // return a safe http(s) image (guards against javascript:/data: in a tag).
    const absolute = new URL(found, pageUrl).href;
    return isHttpUrl(absolute) ? absolute : null;
  } catch {
    return null; // CORS / network / parse failure → graceful fallback
  } finally {
    clearTimeout(timer);
  }
}
